import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { Box, Group, Paper, Text } from '@mantine/core'

type PanelRect = {
  x: number
  y: number
  width: number
  height: number
}

type Props = {
  storageKey: string
  title: ReactNode
  subtitle?: ReactNode
  icon?: ReactNode
  actions?: ReactNode
  opened: boolean
  onClose: () => void
  children: ReactNode
  defaultWidth?: number
  defaultHeight?: number
  minWidth?: number
  minHeight?: number
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function readRect(storageKey: string): PanelRect | null {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PanelRect>
    if (
      typeof parsed.x !== 'number' ||
      typeof parsed.y !== 'number' ||
      typeof parsed.width !== 'number' ||
      typeof parsed.height !== 'number'
    ) {
      return null
    }
    return parsed as PanelRect
  } catch {
    return null
  }
}

function writeRect(storageKey: string, rect: PanelRect) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(rect))
  } catch {
    /* ignore */
  }
}

function defaultRect(defaultWidth: number, defaultHeight: number): PanelRect {
  const viewportWidth = typeof window === 'undefined' ? defaultWidth + 32 : window.innerWidth
  const viewportHeight = typeof window === 'undefined' ? defaultHeight + 80 : window.innerHeight
  const width = Math.min(defaultWidth, Math.max(360, viewportWidth - 32))
  const height = Math.min(defaultHeight, Math.max(420, viewportHeight - 80))
  return {
    x: Math.max(16, viewportWidth - width - 16),
    y: 64,
    width,
    height,
  }
}

function normalizeRect(rect: PanelRect, minWidth: number, minHeight: number): PanelRect {
  const viewportWidth = typeof window === 'undefined' ? rect.width + 32 : window.innerWidth
  const viewportHeight = typeof window === 'undefined' ? rect.height + 80 : window.innerHeight
  const width = clamp(rect.width, minWidth, Math.max(minWidth, viewportWidth - 32))
  const height = clamp(rect.height, minHeight, Math.max(minHeight, viewportHeight - 80))
  return {
    width,
    height,
    x: clamp(rect.x, 8, Math.max(8, viewportWidth - width - 8)),
    y: clamp(rect.y, 56, Math.max(56, viewportHeight - height - 8)),
  }
}

export function FloatingWorkspacePanel({
  storageKey,
  title,
  subtitle,
  icon,
  actions,
  opened,
  onClose,
  children,
  defaultWidth = 960,
  defaultHeight = 720,
  minWidth = 360,
  minHeight = 420,
}: Props) {
  const initialRect = useMemo(
    () => normalizeRect(readRect(storageKey) ?? defaultRect(defaultWidth, defaultHeight), minWidth, minHeight),
    [defaultHeight, defaultWidth, minHeight, minWidth, storageKey],
  )
  const [rect, setRect] = useState<PanelRect>(initialRect)
  const dragRef = useRef<{
    mode: 'move' | 'resize'
    pointerId: number
    startX: number
    startY: number
    startRect: PanelRect
  } | null>(null)

  useEffect(() => {
    setRect(normalizeRect(readRect(storageKey) ?? defaultRect(defaultWidth, defaultHeight), minWidth, minHeight))
  }, [defaultHeight, defaultWidth, minHeight, minWidth, storageKey])

  useEffect(() => {
    if (!opened) return
    setRect((current) => normalizeRect(current, minWidth, minHeight))
  }, [opened, minWidth, minHeight])

  useEffect(() => {
    const onResize = () => setRect((current) => normalizeRect(current, minWidth, minHeight))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [minWidth, minHeight])

  useEffect(() => {
    writeRect(storageKey, rect)
  }, [rect, storageKey])

  const finishDrag = useCallback(() => {
    dragRef.current = null
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }, [])

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag || event.pointerId !== drag.pointerId) return
      event.preventDefault()
      const dx = event.clientX - drag.startX
      const dy = event.clientY - drag.startY
      setRect(() => {
        if (drag.mode === 'move') {
          return normalizeRect(
            {
              ...drag.startRect,
              x: drag.startRect.x + dx,
              y: drag.startRect.y + dy,
            },
            minWidth,
            minHeight,
          )
        }
        return normalizeRect(
          {
            ...drag.startRect,
            width: drag.startRect.width + dx,
            height: drag.startRect.height + dy,
          },
          minWidth,
          minHeight,
        )
      })
    },
    [minHeight, minWidth],
  )

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', finishDrag)
    window.addEventListener('pointercancel', finishDrag)
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', finishDrag)
      window.removeEventListener('pointercancel', finishDrag)
    }
  }, [finishDrag, onPointerMove])

  const startDrag = (mode: 'move' | 'resize') => (event: ReactPointerEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    dragRef.current = {
      mode,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startRect: rect,
    }
    document.body.style.userSelect = 'none'
    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'nwse-resize'
  }

  if (!opened) return null

  return (
    <>
      <Box
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 445,
          background: 'rgba(15, 23, 42, 0.28)',
          backdropFilter: 'blur(2px)',
        }}
      />
      <Paper
        shadow="xl"
        radius="lg"
        style={{
          position: 'fixed',
          left: rect.x,
          top: rect.y,
          width: rect.width,
          height: rect.height,
          zIndex: 450,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          border: '1px solid light-dark(rgba(148, 163, 184, 0.35), rgba(255, 255, 255, 0.08))',
          background: 'light-dark(#f8fafc, var(--mantine-color-dark-8))',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <Group
          justify="space-between"
          px="lg"
          py="md"
          wrap="nowrap"
          onPointerDown={startDrag('move')}
          style={{
            cursor: 'grab',
            borderBottom: '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5))',
            background:
              'radial-gradient(circle at 0 0, light-dark(rgba(59,130,246,0.16), rgba(59,130,246,0.16)), transparent 36%), light-dark(#ffffff, var(--mantine-color-dark-7))',
          }}
        >
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, pointerEvents: 'none' }}>
            {icon}
            <Box style={{ minWidth: 0 }}>
              <Box
                style={{
                  minWidth: 0,
                  fontWeight: 700,
                  fontSize: 'var(--mantine-font-size-md)',
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Box>
              {subtitle ? (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {subtitle}
                </Text>
              ) : null}
            </Box>
          </Group>
          <Group gap={4} wrap="nowrap" style={{ pointerEvents: 'auto' }}>
            {actions}
          </Group>
        </Group>
        <Box
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
        <Box
          onPointerDown={startDrag('resize')}
          title="Изменить размер"
          style={{
            position: 'absolute',
            right: 6,
            bottom: 6,
            width: 18,
            height: 18,
            cursor: 'nwse-resize',
            borderRight: '2px solid var(--mantine-color-dimmed)',
            borderBottom: '2px solid var(--mantine-color-dimmed)',
            opacity: 0.45,
            borderBottomRightRadius: 6,
          }}
        />
      </Paper>
    </>
  )
}
